import { create } from 'zustand';

/* ================================================================== */
/*  Types                                                             */
/* ================================================================== */

export interface DeployedContract {
  id: string;
  contractName: string;
  address: string;
  network: string;
  chainId: number;
  abi: unknown[];
  bytecode?: string;
  deployedAt: number;       // timestamp
  transactionHash?: string;
  blockNumber?: number;
  gasUsed?: string;
  workspaceId: string;

  // Interaction history
  interactions: ContractInteraction[];
}

export interface ContractInteraction {
  id: string;
  type: 'read' | 'write';
  functionName: string;
  args: string[];
  result?: string;
  error?: string;
  txHash?: string;
  gasUsed?: string;
  timestamp: number;
}

/* ================================================================== */
/*  Store                                                             */
/* ================================================================== */

interface ContractState {
  contracts: DeployedContract[];
  activeContractId: string | null;

  // Actions
  addContract: (contract: Omit<DeployedContract, 'id' | 'interactions'>) => string;
  removeContract: (id: string) => void;
  setActiveContract: (id: string | null) => void;
  addInteraction: (contractId: string, interaction: Omit<ContractInteraction, 'id' | 'timestamp'>) => void;
  clearInteractions: (contractId: string) => void;
  getActiveContract: () => DeployedContract | null;
}

let nextId = 1;
let nextInteractionId = 1;

export const useContractStore = create<ContractState>((set, get) => ({
  contracts: [],
  activeContractId: null,

  addContract: (data) => {
    const id = `contract-${nextId++}`;
    const contract: DeployedContract = {
      ...data,
      id,
      interactions: [],
    };

    set((s) => ({
      contracts: [contract, ...s.contracts].slice(0, 50), // keep max 50
      activeContractId: id,
    }));

    return id;
  },

  removeContract: (id) => {
    set((s) => ({
      contracts: s.contracts.filter((c) => c.id !== id),
      activeContractId: s.activeContractId === id ? null : s.activeContractId,
    }));
  },

  setActiveContract: (id) => set({ activeContractId: id }),

  addInteraction: (contractId, interaction) => {
    const entry: ContractInteraction = {
      ...interaction,
      id: `ix-${nextInteractionId++}`,
      timestamp: Date.now(),
    };

    set((s) => ({
      contracts: s.contracts.map((c) =>
        c.id === contractId
          ? { ...c, interactions: [entry, ...c.interactions].slice(0, 100) }
          : c,
      ),
    }));
  },

  clearInteractions: (contractId) => {
    set((s) => ({
      contracts: s.contracts.map((c) =>
        c.id === contractId ? { ...c, interactions: [] } : c,
      ),
    }));
  },

  getActiveContract: () => {
    const { contracts, activeContractId } = get();
    return contracts.find((c) => c.id === activeContractId) ?? null;
  },
}));
